import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, User, ChevronDown, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NotificationCenter from './NotificationCenter';
import { useTranslation } from 'react-i18next';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n?.language || 'sr';

  const switchLanguage = (lang) => {
    i18n?.changeLanguage(lang);
    localStorage.setItem('valuebook_language', lang);
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => switchLanguage('sr')}
        title="Srpski"
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold transition-all ${
          currentLang === 'sr' ?'border border-yellow-600 shadow-sm' :'text-gray-500 hover:bg-gray-100 border border-transparent'
        }`}
        style={currentLang === 'sr' ? { backgroundColor: '#F5EBB9', color: '#7A6A1E' } : {}}
      >
        <span className="text-base leading-none">🇷🇸</span>
        <span className="hidden sm:inline">SRB</span>
      </button>
      <button
        onClick={() => switchLanguage('en')}
        title="English"
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold transition-all ${
          currentLang === 'en' ?'border border-yellow-600 shadow-sm' :'text-gray-500 hover:bg-gray-100 border border-transparent'
        }`}
        style={currentLang === 'en' ? { backgroundColor: '#F5EBB9', color: '#7A6A1E' } : {}}
      >
        <span className="text-base leading-none">🇬🇧</span>
        <span className="hidden sm:inline">ENG</span>
      </button>
    </div>
  );
};

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut, userRole } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Navigation] Mounted with auth state:', {
        hasUser: !!user,
        hasProfile: !!profile,
        role: userRole
      });
    }
  }, []);

  const navigationItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.marketplace'), path: '/berza' },
    { name: t('nav.trading'), path: '/zapocni-trgovinu' },
    { name: t('nav.goldClause'), path: '/zlatna-klauzula', isGold: true },
    { name: t('nav.community'), path: '/zajednica' },
    { name: t('nav.admin'), path: '/admin', requiresAdmin: true }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setIsProfileDropdownOpen(false);
      navigate('/register');
    }
  };

  const handleNavigationClick = (item) => {
    if (item?.action === 'logout') {
      handleLogout();
      setIsMenuOpen(false);
    } else {
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50" style={{ borderBottomColor: '#e8d56a33' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── SINGLE ROW: logo + nav links + auth ── */}
        <div className="flex items-center justify-between h-14">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-shrink-0">
            <img
              src="/assets/images/ZnaK_ValueBook_prilagodjen-1770155460741.png"
              alt="ValueBook Trademark Logo"
              className="w-10 h-10 object-contain"
            />
            <div className="flex flex-col items-center">
              <span className="text-xl font-bold" style={{ color: '#A8922A' }}>ValueBook</span>
            </div>
          </Link>

          {/* Desktop Nav Links + Auth */}
          <div className="hidden md:flex items-center gap-3">
            <nav className="flex items-center gap-6">
              {navigationItems?.map((item) => {
                if (item?.requiresAdmin && profile?.role !== 'admin') return null;
                if (item?.requiresAuth && !user) return null;

                const isActive = isActivePath(item?.path);

                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`text-sm font-semibold transition-colors flex items-center gap-1.5 py-1 border-b-2 ${
                      isActive ? 'pb-0.5' : 'border-transparent'
                    }`}
                    style={
                      item?.isGold || item?.path === '/berza'
                        ? {
                            color: '#A8922A',
                            borderColor: isActive ? '#A8922A' : 'transparent'
                          }
                        : isActive
                          ? { color: '#23395B', borderColor: '#23395B' }
                          : { color: '#374151' }
                    }
                    onMouseEnter={e => {
                      if (!item?.isGold && item?.path !== '/berza') {
                        e.currentTarget.style.color = '#23395B';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!item?.isGold && item?.path !== '/berza' && !isActive) {
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                  >
                    {item?.requiresAdmin && <Shield size={15} />}
                    {item?.name}
                  </Link>
                );
              })}
            </nav>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Auth: login button or user profile */}
            {!user && (
              <Link
                to="/register"
                className="px-3 py-1.5 text-white rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
                style={{ backgroundColor: '#23395B' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#162440'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#23395B'}
              >
                {t('nav.loginRegister')}
              </Link>
            )}

            {user && (
              <div className="flex items-center gap-3">
                <NotificationCenter />

                <div className="relative">
                  <button
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 text-white rounded-lg transition-colors"
                    style={{ backgroundColor: '#23395B' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#162440'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#23395B'}
                  >
                    <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: '#A8922A', color: '#ffffff' }}>
                      {profile?.fullName?.charAt(0)?.toUpperCase() || profile?.email?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-xs" style={{ color: '#D6E0EE' }}>{t('nav.valuebookId')}</span>
                      <span className="text-xs font-bold">{profile?.companyId || user?.user_metadata?.company_id || profile?.email || user?.email}</span>
                    </div>
                    <ChevronDown size={14} className="text-white" />
                  </button>

                  {isProfileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">{profile?.fullName || user?.user_metadata?.full_name || 'User'}</p>
                        <p className="text-xs text-gray-500">{profile?.email || user?.email}</p>
                        {(profile?.companyId || user?.user_metadata?.company_id) && (
                          <p className="text-xs mt-1" style={{ color: '#A8922A' }}>ValueBook ID: {profile?.companyId || user?.user_metadata?.company_id}</p>
                        )}
                      </div>

                      <Link
                        to="/profil"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px]"
                        onClick={() => setIsProfileDropdownOpen(false)}
                      >
                        <User size={16} />
                        {t('nav.myProfile')}
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors min-h-[44px]"
                      >
                        <LogOut size={16} />
                        {t('nav.logout')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile: Language switcher + Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2.5 rounded-lg hover:bg-gray-100 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      {isMenuOpen && (
        <div className="md:hidden border-t bg-white" style={{ borderTopColor: '#e8d56a33' }}>
          <div className="px-4 py-3 space-y-1">
            {navigationItems?.map((item) => {
              if (item?.requiresAdmin && profile?.role !== 'admin') return null;
              if (item?.requiresAuth && !user) return null;

              const isActive = isActivePath(item?.path);

              return (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center gap-2 px-3 rounded-lg text-sm font-medium transition-colors min-h-[44px]`}
                  style={
                    isActive
                      ? { backgroundColor: '#F5EBB9', color: '#A8922A' }
                      : item?.isGold || item?.path === '/berza'
                        ? { color: '#A8922A' }
                        : { color: '#374151' }
                  }
                  onClick={() => handleNavigationClick(item)}
                >
                  {item?.requiresAdmin && <Shield size={16} />}
                  {item?.name}
                </Link>
              );
            })}

            {user && (
              <>
                <div className="border-t border-gray-200 pt-3 mt-2">
                  <Link
                    to="/profil"
                    className="flex items-center gap-2 px-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User size={16} />
                    {t('nav.myProfile')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] text-red-600 hover:bg-red-50"
                  >
                    <LogOut size={16} />
                    {t('nav.logout')}
                  </button>
                </div>
              </>
            )}

            {!user && (
              <div className="border-t border-gray-200 pt-3 mt-2">
                <Link
                  to="/register"
                  className="flex items-center justify-center px-3 py-2.5 rounded-lg text-sm font-medium text-white transition-colors min-h-[44px]"
                  style={{ backgroundColor: '#23395B' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.loginRegister')}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;